/**
 * context/app/useAccountActions.js - account create / update / delete and
 * opportunity delete.
 * Moved verbatim out of AppProvider (AppContext.jsx), which calls it at the
 * same point (same hook order) with the state it reads.
 */
import { useCallback } from 'react';
import { toSnake } from './rows';
import { API_BASE } from './apiClient';

export function useAccountActions({ refreshQuietly, dropProject }) {
  // -- ACCOUNTS -----------------------------------------------
  const addAccount = useCallback(
    async (d) => {
      try {
        await fetch(`${API_BASE}/api/accounts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(toSnake(d)),
        });
        await refreshQuietly(['accounts']);
      } catch (err) {
        console.error('addAccount error:', err);
      }
    },
    [refreshQuietly]
  );

  // Returns { ok, error }. A refused change (e.g. "log a meeting before
  // Discovery Meeting Complete", or a Customer set back to Lead) used to be
  // silent - the screens now show the server's reason.
  const updateAccount = useCallback(
    async (id, updates) => {
      try {
        const res = await fetch(`${API_BASE}/api/accounts/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(toSnake(updates)),
        });
        // the accounts list alone: an account edit moves no other list
        if (res.ok) {
          await refreshQuietly(['accounts']);
          return { ok: true };
        }
        if (res.status === 404) {
          const payload = { ...updates, id };
          const made = await fetch(`${API_BASE}/api/accounts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(toSnake(payload)),
          });
          await refreshQuietly(['accounts']);
          return { ok: made.ok };
        }
        const body = await res.json().catch(() => ({}));
        return { ok: false, error: body.error || 'Could not save - try again.', code: body.code };
      } catch (err) {
        console.error('updateAccount error:', err);
        return { ok: false, error: 'Could not save - try again.' };
      }
    },
    [refreshQuietly]
  );

  const deleteAccount = useCallback(
    async (id) => {
      try {
        await fetch(`${API_BASE}/api/accounts/${id}`, { method: 'DELETE' });
        // deleting an account unlinks its opportunities (routes/accounts.js)
        await refreshQuietly(['accounts', 'projects']);
      } catch (err) {
        console.error('deleteAccount error:', err);
      }
    },
    [refreshQuietly]
  );

  // -- OPPORTUNITIES -------------------------------------------
  const deleteOpportunity = useCallback(
    async (id) => {
      try {
        const res = await fetch(`${API_BASE}/api/projects/${id}`, { method: 'DELETE' });
        if (res.ok) dropProject(id);
        refreshQuietly(['projects', 'accounts']);
      } catch (err) {
        console.error('deleteOpportunity error:', err);
      }
    },
    [dropProject, refreshQuietly]
  );
  return { addAccount, updateAccount, deleteAccount, deleteOpportunity };
}
